import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectType } from '@core/enums/select';
import { ISchoolYear } from '@core/interfaces/iratingperiod';
import { IStateDashboardSearchFields, IStateDashboardSearchRequest } from '@core/interfaces/istate-dashboard';
import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { SelectAgeGradeComponent } from '@shared/components/dropdowns/select-age-grade/select-age-grade.component';
import { SelectDisabilityComponent } from '@shared/components/dropdowns/select-disability/select-disability.component';
import { SelectGlobalRatingComponent } from '@shared/components/dropdowns/select-global-rating/select-global-rating.component';
import { SelectLanguageComponent } from '@shared/components/dropdowns/select-language/select-language.component';
import { Subscription, take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-dashboard-search',
    templateUrl: './dashboard-search.component.html',
    styleUrls: ['./dashboard-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardSearchComponent implements OnInit {
  @ViewChild('ageGroupInstrumentSelect ') ageGroupInstrumentSelect : SelectAgeGradeComponent | undefined;
  @ViewChild('homeLanguageSelect') homeLanguageSelect: SelectLanguageComponent | undefined;
  @ViewChild('globalRatingsSelect') globalRatingsSelect: SelectGlobalRatingComponent | undefined;
  @ViewChild('disabilitySelect') disabilitySelect: SelectDisabilityComponent | undefined;

  fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private lookup = inject(LookupService);
  private ratingPeriod = inject(RatingPeriodService);
  private translocoService = inject(TranslocoService);

  @Input() title: string = '';
  @Input() downloadBtnText: string = this.translocoService.translate('user.stateDashboard.search.export');
  @Input() distinctPrograms: boolean = false;
  @Input() displayDropdowns: IStateDashboardSearchFields = {
    ageGroup: false,
    enrollmentProgram: false,
    homeLanguage: false,
    schoolYear: false,
    globalRatingPeriods: false,
    disability: false
  };
  @Input() requiredFields: IStateDashboardSearchFields = {
    ageGroup: false,
    enrollmentProgram: false,
    homeLanguage: false,
    globalRatingPeriods: false,
    disability: false
  };
  @Output() exportClicked: EventEmitter<IStateDashboardSearchRequest> = new EventEmitter<IStateDashboardSearchRequest>();

  searchForm: FormGroup | any;
  user = this.authService.getCurrentUser();
  subscriptions = new Subscription();
  clearDropdowns: boolean = false;
  schoolYearId: number | null = null;
  public get selectType() { return SelectType; }

  get stateId() { return this.searchForm.get('stateId'); }
  get agencyId() { return this.searchForm.get('agencyId'); }
  get isGlobalAgencies() { return this.searchForm.get('isGlobalAgencies'); }
  get ageGroupId() { return this.searchForm.get('ageGroupId'); }
  get enrollmentProgramFilterIds() { return this.searchForm.get('enrollmentProgramFilterIds'); }
  get homeLanguageIds() { return this.searchForm.get('homeLanguageIds'); }
  get globalRatingPeriodIds() { return this.searchForm.get('globalRatingPeriodIds'); }
  get disabilityIds() { return this.searchForm.get('disabilityIds'); }
  get siteLevelCountyIds() { return this.searchForm.get('siteLevelCountyIds'); }

  ngOnInit(): void {
    this.initializeForm();
    this.getCurrentSchoolYear();
  }

  private initializeForm(): void {
    this.searchForm = this.fb.group({
      stateId: [this.user.stateId, Validators.required],
      agencyId: [this.user.agencyId, Validators.required],
      isGlobalAgencies: [false],
      ageGroupId: [null, this.requiredFields.ageGroup ? Validators.required : null],
      enrollmentProgramFilterIds: [[], this.requiredFields.enrollmentProgram ? Validators.required : null],
      homeLanguageIds: [[], this.requiredFields.homeLanguage ? Validators.required : null],
      globalRatingPeriodIds: [[], this.requiredFields.globalRatingPeriods ? Validators.required : null],
      disabilityIds: [[], this.requiredFields.disability ? Validators.required : null],
      siteLevelCountyIds: [[]],
    });

    this.subscriptions.add(
      this.isGlobalAgencies.valueChanges.subscribe((val: boolean) => {
        if (val) this.agencyId.clearValidators();
        else this.agencyId.setValidators([Validators.required]);
        this.agencyId.setValue(null);
      })
    );
  }

  getCurrentSchoolYear() {
    this.ratingPeriod.getActiveSchoolYear().pipe(take(1)).subscribe(schoolYear => {
      this.schoolYearId = schoolYear?.id;
    });
  }

  handleDropdown(event: any, type: string): void {
    switch (type) {
      case SelectType.Agencies:
        this.agencyId.setValue(event?.id);
        break;
      case SelectType.AgeInstrument:
        this.ageGroupId.setValue(event?.id);
        this.enrollmentProgramFilterIds.setValue([]);
        break;
      case SelectType.EnrollmentProgram:
        event.length
          ? this.enrollmentProgramFilterIds.setValue(event.map((program: any) => program.id))
          : this.enrollmentProgramFilterIds.setValue([]);
        break;
      case SelectType.Language:
        event.length
          ? this.homeLanguageIds.setValue(event.map((language: any) => language.id))
          : this.homeLanguageIds.setValue([]);
        break;
      case SelectType.GlobalRatingPeriods:
        event.length
          ? this.globalRatingPeriodIds.setValue(event.map((grp: any) => grp.id))
          : this.globalRatingPeriodIds.setValue([]);
        break;
      case SelectType.PrimaryDisability:
        event.length
          ? this.disabilityIds.setValue(event.map((disability: any) => disability.id))
          : this.disabilityIds.setValue([]);
        break;
      case SelectType.County:
        event.length
          ? this.siteLevelCountyIds.setValue(event.map((county: any) => county.id))
          : this.siteLevelCountyIds.setValue([]);
        break;
      default:
        break;
    }
  }

  handleExport(): void {
    this.exportClicked.emit(this.searchForm.value);
  }

  clear(): void {
    this.clearDropdowns = true;
    this.searchForm.reset();
    this.ageGroupInstrumentSelect?.clearSelected();
    this.homeLanguageSelect?.clearSelected();
    this.globalRatingsSelect?.clearSelected();
    this.disabilitySelect?.clearSelected();
    
    this.stateId.setValue(this.user.stateId);
    this.isGlobalAgencies.setValue(false);
    this.enrollmentProgramFilterIds.setValue([]);
    this.homeLanguageIds.setValue([]);
    this.globalRatingPeriodIds.setValue([]);
    this.disabilityIds.setValue([]);
    this.siteLevelCountyIds.setValue([]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
